import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Plus, X, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { calculateSpreadCost, formatSpreadDisplay } from '../constants/spreadConfig';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';
import TakeProfitStopLossModal from './TakeProfitStopLossModal';
import { useListedCfdQuote } from '../hooks/useListedCfdQuote';
import { getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';
import ListedCfdQuoteNotice from './ListedCfdQuoteNotice';

interface UnifiedTradingPanelProps {
  selectedPair: string;
  usdtBalance: number;
  availableBalance?: number;
  maxAllowedLeverage?: number;
  minAllowedLeverage?: number;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onTrade: (
    symbol: string,
    side: 'long' | 'short',
    amount: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => void;
}

const UnifiedTradingPanel: React.FC<UnifiedTradingPanelProps> = ({
  selectedPair,
  usdtBalance,
  availableBalance,
  maxAllowedLeverage = 100,
  minAllowedLeverage = 1,
  calculateLiquidationPrice,
  onTrade
}) => {
  const { t } = useTranslation();
  const { getPriceBySymbol, lastUpdateTime } = useUnifiedPrices();
  const { listing, quoteAvailable, referenceQuote } = useListedCfdQuote(selectedPair);

  const isLeverageLocked = minAllowedLeverage === maxAllowedLeverage;

  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('cross');
  const [leverage, setLeverage] = useState(() => Math.max(10, minAllowedLeverage));
  const [orderType, setOrderType] = useState<'limit' | 'market'>('limit');
  const [inputMode, setInputMode] = useState<'lots' | 'usd'>('lots');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [sliderValue, setSliderValue] = useState(0);
  const [showLeverageDropdown, setShowLeverageDropdown] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const leverageDropdownRef = useRef<HTMLDivElement>(null);

  const [stopLoss, setStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [takeProfit, setTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [showSLModal, setShowSLModal] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const [activeSide, setActiveSide] = useState<'long' | 'short'>('long');

  // A price/stop entered for one underlying must not carry into another.
  useEffect(() => {
    setLimitPrice('');
    setAmount('');
    setSliderValue(0);
    setStopLoss(null);
    setTakeProfit(null);
  }, [selectedPair]);

  useEffect(() => {
    if (isLeverageLocked) {
      setLeverage(minAllowedLeverage);
    } else if (leverage < minAllowedLeverage) {
      setLeverage(minAllowedLeverage);
    } else if (leverage > maxAllowedLeverage) {
      setLeverage(maxAllowedLeverage);
    }
  }, [minAllowedLeverage, maxAllowedLeverage, isLeverageLocked]);

  const livePairPrice = useMemo(() => {
    return getPriceBySymbol(selectedPair);
  }, [getPriceBySymbol, selectedPair, lastUpdateTime]);

  const isCfdPair = useMemo(() => {
    return CFD_INSTRUMENTS.some(inst => inst.symbol === selectedPair);
  }, [selectedPair]);

  const getLotSize = useCallback((symbol: string): number => {
    const listing = getListedCfdInstrument(symbol);
    if (listing) return listing.lotSize;
    const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
    if (instrument && instrument.type === 'forex') {
      if (symbol.endsWith("JPY")) return 1000;
      return 100000;
    } else if (instrument && instrument.type === 'commodity') {
      switch (symbol) {
        case 'XAG/USD': return 5000;
        case 'XAUUSD':
        case 'XAU/USD': return 100;
        case 'XPTUSD':
        case 'XPT/USD': return 100;
        case 'XPDUSD':
        case 'XPD/USD': return 100;
        case 'NATGAS/USD': return 10000;
        case 'BCO/USD': return 1000;
        case 'WTICO/USD': return 1000;
        case 'CORN/USD': return 5000;
        case 'WHEAT/USD': return 5000;
        case 'SUGAR/USD': return 112000;
        default: return 1;
      }
    } else if (instrument && instrument.type === 'stock') {
      return 100;
    }
    return 1;
  }, []);

  const convertUsdToLots = useCallback((usdAmount: number): number => {
    if (!isCfdPair) return usdAmount;
    const lotSize = getLotSize(selectedPair);
    if (!livePairPrice || livePairPrice <= 0) return 0;
    return usdAmount / (livePairPrice * lotSize);
  }, [isCfdPair, selectedPair, livePairPrice, getLotSize]);

  const getLotsFromInput = useCallback((inputValue: string): number => {
    const amt = parseFloat(inputValue) || 0;
    if (!isCfdPair || inputMode === 'lots') {
      return amt;
    } else {
      return convertUsdToLots(amt);
    }
  }, [isCfdPair, inputMode, convertUsdToLots]);

  useEffect(() => {
    if (livePairPrice > 0 && !limitPrice) {
      setLimitPrice(livePairPrice.toFixed(2));
    }
  }, [livePairPrice, limitPrice]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (leverageDropdownRef.current && !leverageDropdownRef.current.contains(event.target as Node)) {
        setShowLeverageDropdown(false);
      }
    };

    if (showLeverageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLeverageDropdown]);

  const balance = availableBalance !== undefined ? availableBalance : usdtBalance;
  const baseAsset = selectedPair.replace('USDT', '').replace('/USD', '');
  const lotSize = isCfdPair ? getLotSize(selectedPair) : 1;
  const lotsAmount = getLotsFromInput(amount);

  const calculateAmountFromSlider = useCallback((percentage: number) => {
    if (percentage <= 0 || livePairPrice <= 0) return '';
    const targetMargin = balance * (percentage / 100);
    if (isCfdPair) {
      const calculatedLots = (targetMargin * leverage) / (livePairPrice * lotSize);
      return calculatedLots.toFixed(5);
    }
    const calculatedAmount = (targetMargin * leverage) / livePairPrice;
    return calculatedAmount.toFixed(6);
  }, [balance, leverage, livePairPrice, isCfdPair, lotSize]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    const newAmount = calculateAmountFromSlider(value);
    setAmount(newAmount);
  };

  const calculateNotionalValue = () => {
    if (isCfdPair) {
      return lotsAmount * livePairPrice * lotSize;
    }
    const amt = parseFloat(amount) || 0;
    return amt * livePairPrice;
  };

  const calculateCost = () => {
    const notional = calculateNotionalValue();
    const margin = notional / leverage;
    if (isCfdPair) {
      const spread = lotsAmount > 0 ? calculateSpreadCost(selectedPair, livePairPrice, lotsAmount, lotSize) : 0;
      return margin + spread;
    }
    const spread = parseFloat(amount) > 0 ? calculateSpreadCost(selectedPair, livePairPrice, parseFloat(amount), 1) : 0;
    return margin + spread;
  };

  const handleTrade = (side: 'long' | 'short') => {
    if (!quoteAvailable || !Number.isFinite(livePairPrice) || livePairPrice <= 0) {
      setErrorMessage('A current quote is unavailable. Please try again when the market is open.');
      return;
    }
    if (listing && orderType === 'limit' && (!Number.isFinite(Number(limitPrice)) || Number(limitPrice) <= 0)) {
      setErrorMessage('Enter a valid USD limit price.');
      return;
    }
    if (isCfdPair) {
      if (!lotsAmount || lotsAmount <= 0) {
        setErrorMessage('Please enter a valid amount');
        return;
      }

      const cost = calculateCost();
      if (cost > balance) {
        setErrorMessage('Insufficient available balance');
        return;
      }

      const price = orderType === 'market' ? livePairPrice : parseFloat(limitPrice) || livePairPrice;
      onTrade(selectedPair, side, lotsAmount * lotSize, leverage, marginType, stopLoss || undefined, takeProfit || undefined, orderType, price);
      setSuccessMessage(`${side === 'long' ? 'Long' : 'Short'} position opened for ${lotsAmount.toFixed(5)} lots`);
    } else {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) {
        setErrorMessage('Please enter a valid amount');
        return;
      }

      const cost = calculateCost();
      if (cost > balance) {
        setErrorMessage('Insufficient available balance');
        return;
      }

      const price = orderType === 'market' ? livePairPrice : parseFloat(limitPrice) || livePairPrice;
      onTrade(selectedPair, side, amt, leverage, marginType, stopLoss || undefined, takeProfit || undefined, orderType, price);
      setSuccessMessage(`${side === 'long' ? 'Buy/Long' : 'Sell/Short'} order placed successfully`);
    }
    setAmount('');
    setSliderValue(0);
    setStopLoss(null);
    setTakeProfit(null);
  };

  const openSLModal = (side: 'long' | 'short') => {
    setActiveSide(side);
    setShowSLModal(true);
  };

  const openTPModal = (side: 'long' | 'short') => {
    setActiveSide(side);
    setShowTPModal(true);
  };

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const leverageOptions = [1, 2, 3, 5, 10, 20, 25, 50, 75, 100, 125].filter(l => l >= minAllowedLeverage && l <= maxAllowedLeverage);

  return (
    <div className="h-full flex flex-col bg-white overflow-y-auto">
      <div className="p-4 space-y-4">
        {listing && (
          <ListedCfdQuoteNotice symbol={selectedPair} quoteAvailable={quoteAvailable} referenceQuote={referenceQuote} />
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">Trade</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMarginType(marginType === 'cross' ? 'isolated' : 'cross')}
              className="px-3 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
            >
              {marginType === 'cross' ? 'Cross' : 'Isolated'}
            </button>
            {isLeverageLocked ? (
              <span className="px-3 py-1 text-xs font-medium rounded bg-slate-200 text-slate-500 cursor-default">
                {leverage}.00x
              </span>
            ) : (
              <div className="relative" ref={leverageDropdownRef}>
                <button
                  onClick={() => setShowLeverageDropdown(!showLeverageDropdown)}
                  className="px-3 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors flex items-center gap-1"
                >
                  {leverage}.00x
                  <ChevronDown size={12} />
                </button>
                {showLeverageDropdown && (
                  <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 min-w-[80px]">
                    {leverageOptions.map(lev => (
                      <button
                        key={lev}
                        onClick={() => {
                          setLeverage(lev);
                          setShowLeverageDropdown(false);
                        }}
                        className={`w-full px-3 py-1.5 text-xs text-left hover:bg-slate-50 ${
                          leverage === lev ? 'bg-blue-50 text-blue-600' : 'text-slate-700'
                        }`}
                      >
                        {lev}x
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg flex-1">
            {(['limit', 'market'] as const).map(type => (
              <button
                key={type}
                onClick={() => setOrderType(type)}
                className={`flex-1 py-1.5 text-xs font-medium rounded transition-colors capitalize ${
                  orderType === type
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {isCfdPair && (
            <div className="flex p-0.5 bg-slate-100 rounded-lg">
              <button
                onClick={() => setInputMode('lots')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  inputMode === 'lots'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Lots
              </button>
              <button
                onClick={() => setInputMode('usd')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  inputMode === 'usd'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                USD
              </button>
            </div>
          )}
        </div>

        {orderType !== 'market' && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Price {listing?.type === 'index' ? '(points)' : listing ? '(USD)' : ''}</label>
            <div className="relative">
              <input
                type="text"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{listing?.type === 'index' ? 'PTS' : 'USDT'}</span>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-slate-500">
              {isCfdPair ? `Total (${inputMode === 'lots' ? 'Lots' : 'USD'})` : `Total(${baseAsset})`}
            </label>
            <button className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
              <ChevronDown size={12} />
            </button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setSliderValue(0);
              }}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isCfdPair && inputMode === 'lots' ? "0.01" : "0.000000"}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {isCfdPair ? (inputMode === 'lots' ? 'LOTS' : 'USD') : baseAsset}
            </span>
          </div>
          {isCfdPair && inputMode === 'usd' && amount && lotsAmount > 0 && (
            <div className="mt-1 text-xs text-slate-500">
              = {lotsAmount.toFixed(5)} lots
            </div>
          )}
        </div>

        <div className="px-1">
          <input
            type="range"
            min="0"
            max="100"
            value={sliderValue}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${sliderValue}%, #e2e8f0 ${sliderValue}%, #e2e8f0 100%)`
            }}
          />
          <div className="flex justify-between mt-1 text-xs text-slate-400">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500">Value(USDT)</span>
            <span className="text-slate-900 font-mono">{calculateNotionalValue().toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Cost(USDT)</span>
            <span className="text-slate-900 font-mono">{calculateCost().toFixed(2)}</span>
          </div>
        </div>

        {amount && (isCfdPair ? lotsAmount > 0 : parseFloat(amount) > 0) && (
          <div className="flex gap-2">
            <button
              onClick={() => openSLModal('long')}
              className={`flex-1 py-2.5 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                stopLoss
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
              }`}
            >
              {stopLoss ? (
                <>
                  <Pencil size={12} />
                  SL: ${stopLoss.trigger_price.toFixed(2)}
                  <X
                    size={12}
                    className="ml-1 hover:text-red-700"
                    onClick={(e) => { e.stopPropagation(); setStopLoss(null); }}
                  />
                </>
              ) : (
                <>
                  <Plus size={12} />
                  Stop Loss
                </>
              )}
            </button>
            <button
              onClick={() => openTPModal('long')}
              className={`flex-1 py-2.5 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                takeProfit
                  ? 'bg-green-50 text-green-600 border border-green-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
              }`}
            >
              {takeProfit ? (
                <>
                  <Pencil size={12} />
                  TP: ${takeProfit.trigger_price.toFixed(2)}
                  <X
                    size={12}
                    className="ml-1 hover:text-green-700"
                    onClick={(e) => { e.stopPropagation(); setTakeProfit(null); }}
                  />
                </>
              ) : (
                <>
                  <Plus size={12} />
                  Take Profit
                </>
              )}
            </button>
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={() => handleTrade('long')}
            disabled={!quoteAvailable || livePairPrice <= 0}
            className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors shadow-sm"
          >
            Buy/Long
          </button>
          <button
            onClick={() => handleTrade('short')}
            disabled={!quoteAvailable || livePairPrice <= 0}
            className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors shadow-sm"
          >
            Sell/Short
          </button>
        </div>

        {(errorMessage || successMessage) && (
          <div className={`p-2 rounded-lg text-xs ${
            errorMessage ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
          }`}>
            {errorMessage || successMessage}
          </div>
        )}

        <div className="border-t border-slate-200 pt-4">
          <button className="w-full flex items-center justify-between text-sm text-slate-600 hover:text-slate-900">
            <span>Contract Details</span>
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500">Index Price</span>
            <span className="text-slate-900 font-mono">{livePairPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Mark Price</span>
            <span className="text-slate-900 font-mono">{livePairPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Liq. Price (Long)</span>
            <span className="text-red-500 font-mono">
              {calculateLiquidationPrice('long', livePairPrice, leverage, marginType).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Liq. Price (Short)</span>
            <span className="text-red-500 font-mono">
              {calculateLiquidationPrice('short', livePairPrice, leverage, marginType).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <TakeProfitStopLossModal
        isOpen={showSLModal}
        onClose={() => setShowSLModal(false)}
        type="stopLoss"
        side={activeSide}
        entryPrice={livePairPrice}
        amount={isCfdPair ? lotsAmount : (parseFloat(amount) || 0)}
        leverage={leverage}
        lotSize={isCfdPair ? lotSize : undefined}
        onConfirm={(price, type, execPrice) => {
          if (price > 0) {
            setStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
          } else {
            setStopLoss(null);
          }
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showTPModal}
        onClose={() => setShowTPModal(false)}
        type="takeProfit"
        side={activeSide}
        entryPrice={livePairPrice}
        amount={isCfdPair ? lotsAmount : (parseFloat(amount) || 0)}
        leverage={leverage}
        lotSize={isCfdPair ? lotSize : undefined}
        onConfirm={(price, type, execPrice) => {
          if (price > 0) {
            setTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
          } else {
            setTakeProfit(null);
          }
        }}
      />
    </div>
  );
};

export default UnifiedTradingPanel;
